import { useEffect, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useMe } from "@/hooks/useMe";
import { useAuthStore } from "@/store/useAuthStore";
import { ApiClientError, UnauthenticatedError } from "@/api/client";
import { LoadingSkeleton } from "./LoadingSkeleton";
import { ForbiddenState } from "./ForbiddenState";

interface Props {
  children: ReactNode;
}

const AUTH_DEBUG = true;
function debug(...args: unknown[]) {
  if (AUTH_DEBUG) console.warn("[KORMAX-AUTH]", "[ProtectedRoute]", ...args);
}

export function ProtectedRoute({ children }: Props) {
  const location = useLocation();
  const { data, isPending, isFetching, isError, error } = useMe();
  const setSession = useAuthStore((s) => s.setSession);
  const credentials = useAuthStore((s) => s.credentials);

  useEffect(() => {
    if (data) setSession(data);
  }, [data, setSession]);

  if (data) return <>{children}</>;

  if (isPending || isFetching) {
    debug("loading", { path: location.pathname, isPending, isFetching });
    return <LoadingSkeleton />;
  }

  if (isError) {
    if (error instanceof UnauthenticatedError) {
      // Defensive: if credentials are still in store (e.g. just persisted by
      // LoginPage), drop them so we don't churn forever.
      if (credentials) {
        debug("UnauthenticatedError but creds present — clearing", {
          userName: credentials.userName,
        });
        useAuthStore.getState().reset();
      }
      debug("Navigate → /login", { from: location.pathname });
      return (
        <Navigate
          to="/login"
          replace
          state={{ from: { pathname: location.pathname, search: location.search, hash: location.hash } }}
        />
      );
    }
    const status = error instanceof ApiClientError ? error.status : undefined;
    if (status === 403) return <ForbiddenState />;
    debug("non-auth error", { name: (error as Error)?.name, message: (error as Error)?.message });
    return <LoadingSkeleton />;
  }

  return <LoadingSkeleton />;
}
